import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getBackendUrl } from '../../lib/env';
import { getBusinessUrl } from '../../lib/domains';

const API_URL = getBackendUrl();

function ensureMeta(name, content, attr = 'name') {
  if (!content) return;
  const selector = attr === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function absoluteLink(value, fallback = '') {
  const text = String(value || '').trim();
  if (!text) return fallback;
  if (text.startsWith('http://') || text.startsWith('https://') || text.startsWith('mailto:') || text.startsWith('tel:')) return text;
  if (text.startsWith('/')) return `https://pay.jjuma.com${text}`;
  return `https://${text}`;
}

export default function BusinessPublic() {
  const params = useParams();
  const username = params.username || '';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!username) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    axios.get(`${API_URL}/api/public/business/${username}`)
      .then((res) => setProfile(res.data?.profile || null))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [username]);

  useEffect(() => {
    if (!profile) return;
    const title = profile.seo_title || profile.business_name || 'Business Profile';
    const description = profile.seo_description || profile.about || '';
    const image = profile.social_share_image || profile.cover_image || profile.logo || '';
    const canonical = getBusinessUrl(`/${profile.username}`);
    document.title = `${title} | Jjuma Pay`;
    ensureMeta('description', description);
    ensureMeta('keywords', (profile.seo_keywords || []).join(', '));
    ensureMeta('og:title', title, 'property');
    ensureMeta('og:description', description, 'property');
    ensureMeta('og:image', image, 'property');
    ensureMeta('og:url', canonical, 'property');
    ensureMeta('og:site_name', 'Jjuma Pay', 'property');
    ensureMeta('og:type', 'profile', 'property');
    ensureMeta('og:image:alt', profile.business_name || profile.username, 'property');
    ensureMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    ensureMeta('twitter:title', title);
    ensureMeta('twitter:description', description);
    ensureMeta('twitter:image', image);
    ensureMeta('twitter:url', canonical);
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical);
  }, [profile]);

  const services = useMemo(() => (profile?.services || []).filter(Boolean), [profile]);
  const mapsUrl = profile?.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(profile.location)}` : '';
  const phone = profile?.phone ? `tel:${profile.phone.replace(/\s+/g, '')}` : '';
  const whatsapp = profile?.whatsapp ? `https://wa.me/${profile.whatsapp.replace(/[^\d]/g, '')}` : '';
  const email = profile?.email ? `mailto:${profile.email}` : '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="w-8 h-8 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
        <Card className="w-full max-w-xl bg-white border border-slate-200 rounded-[28px] shadow-sm">
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Business profile not found</p>
            <p className="text-sm text-slate-500">The requested business profile is unavailable or disabled.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const openLink = (value) => {
    const url = absoluteLink(value);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)]">
      <div className="mx-auto max-w-4xl px-4 py-6 space-y-4">
        <Card className="border border-slate-200 bg-white/90 shadow-sm rounded-[22px]">
          <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-700 font-semibold">Business Profile</p>
              <p className="text-sm text-slate-600 break-all">{getBusinessUrl(`/${profile.username}`)}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" className="border-slate-200" onClick={() => navigator.clipboard.writeText(getBusinessUrl(`/${profile.username}`))}>
                Copy link
              </Button>
              <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => window.location.reload()}>
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px]">
          <div className="h-52 bg-slate-100 relative">
            {profile.cover_image ? <img src={profile.cover_image} alt={profile.business_name} className="h-full w-full object-cover" /> : null}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent" />
            <div className="absolute left-4 bottom-4 flex items-end gap-4">
              <div className="h-20 w-20 rounded-2xl overflow-hidden border-4 border-white bg-white shadow-md">
                {profile.logo ? <img src={profile.logo} alt={profile.business_name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-2xl">{profile.business_name?.charAt(0)?.toUpperCase() || 'B'}</div>}
              </div>
              <div className="text-white">
                <h1 className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{profile.business_name}</h1>
                <p className="text-sm text-white/90">@{profile.username}</p>
              </div>
            </div>
          </div>

          <CardContent className="p-4 sm:p-6 space-y-5">
            <div className="space-y-3">
              <p className="text-sm text-slate-600 leading-7">{profile.about || 'Business details will appear here.'}</p>
              {services.length ? (
                <div className="flex flex-wrap gap-2">
                  {services.slice(0, 12).map((service) => (
                    <span key={service} className="inline-flex items-center rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                      {service}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {phone ? <Button type="button" className="bg-slate-900 hover:bg-slate-800 text-white h-12" onClick={() => window.location.href = phone}>Call</Button> : null}
              {whatsapp ? <Button type="button" className="bg-emerald-700 hover:bg-emerald-800 text-white h-12" onClick={() => window.open(whatsapp, '_blank', 'noopener,noreferrer')}>WhatsApp</Button> : null}
              {email ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => window.location.href = email}>Email</Button> : null}
              {mapsUrl ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => window.open(mapsUrl, '_blank', 'noopener,noreferrer')}>Google Maps</Button> : null}
              {profile.website ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => openLink(profile.website)}>Website</Button> : null}
              {profile.payment_link ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => openLink(profile.payment_link)}>Pay</Button> : null}
              {profile.store_link ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => openLink(profile.store_link)}>Store</Button> : null}
              {profile.vcard_link ? <Button type="button" variant="outline" className="border-slate-200 h-12" onClick={() => openLink(profile.vcard_link)}>vCard</Button> : null}
            </div>

            {profile.opening_hours ? (
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600">
                <p className="font-medium text-slate-900 mb-1">Opening hours</p>
                <p>{profile.opening_hours}</p>
              </div>
            ) : null}

            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-medium text-slate-900 mb-1">Contact</p>
              <div className="space-y-1">
                {profile.location ? <p>{profile.location}</p> : null}
                {profile.phone ? <p>{profile.phone}</p> : null}
                {profile.email ? <p>{profile.email}</p> : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-slate-500 pb-4">
          Powered by Jjuma Pay
        </div>
      </div>
    </div>
  );
}
